import type { UseFormRegisterReturn } from 'react-hook-form'
import { SeriesAvatarUpload } from 'containers/rep-clubs/events/series-avatar-upload'

export const SERIES_NAME_MAX_LENGTH = 80

interface SeriesIdentityFieldsProps {
	/** Unique per form, so the label points at the right input. */
	inputId: string
	nameField: UseFormRegisterReturn
	nameLength: number
	nameError?: string
	avatarUrl: string
	onAvatarChange: (avatarUrl: string) => void
	uploadingAvatar: boolean
	setUploadingAvatar: (uploading: boolean) => void
	disabled?: boolean
	autoFocusName?: boolean
}

/**
 * The series' image and name side by side — the two things that identify a series in lists, invites
 * and notifications. Shared by the create and update dialogs.
 */
export function SeriesIdentityFields({
	inputId,
	nameField,
	nameLength,
	nameError,
	avatarUrl,
	onAvatarChange,
	uploadingAvatar,
	setUploadingAvatar,
	disabled,
	autoFocusName,
}: SeriesIdentityFieldsProps) {
	return (
		<div className="flex items-start gap-5">
			<SeriesAvatarUpload
				value={avatarUrl}
				onChange={onAvatarChange}
				uploading={uploadingAvatar}
				setUploading={setUploadingAvatar}
				disabled={disabled}
			/>
			<div className="min-w-0 flex-1 pt-3">
				<label htmlFor={inputId} className="block text-sm font-medium text-primary-text mb-2">
					Series name
				</label>
				<input
					id={inputId}
					type="text"
					autoFocus={autoFocusName}
					maxLength={SERIES_NAME_MAX_LENGTH}
					placeholder="e.g. Summer Tour 2026"
					aria-invalid={!!nameError}
					aria-describedby={`${inputId}-hint`}
					className="block w-full h-12 px-4 py-3 border-2 border-translucent-plus-border bg-primary-surface/[0.4] backdrop-blur-lg rounded-lg text-primary-text placeholder-white/40 focus:ring-0 focus:border-primary/70 aria-[invalid=true]:border-danger"
					{...nameField}
				/>
				<div id={`${inputId}-hint`} className="mt-2 flex items-start justify-between gap-3 text-xs">
					{nameError ? (
						<span className="text-danger">{nameError}</span>
					) : (
						<span className="text-secondary-text">The image appears on the series page, invites and notifications.</span>
					)}
					<span className="flex-none tabular-nums text-secondary-text">
						{nameLength}/{SERIES_NAME_MAX_LENGTH}
					</span>
				</div>
			</div>
		</div>
	)
}
